<div class="p-6 bg-gray-50 min-h-screen">
  <!-- Page Header -->
  <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6 pb-4 border-b border-gray-200">
    <div>
      <div class="flex items-center gap-3">
        <div class="w-11 h-11 bg-gradient-to-tr from-green-600 to-teal-500 rounded-xl flex items-center justify-center text-white shadow-md shadow-green-200">
          <i class="fas fa-bolt text-xl"></i>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Today at a Glance</h1>
          <p class="text-sm text-gray-500">A 360&deg; snapshot of registrations, appointments, agents, and collections for the selected day</p>
        </div>
      </div>
    </div>

    <div class="mt-4 md:mt-0 flex items-center gap-3">
      <button (click)="loadGlance()" [disabled]="loading"
        class="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm transition-all">
        <i class="fas fa-sync-alt mr-2 text-gray-500" [class.fa-spin]="loading"></i> Refresh
      </button>
    </div>
  </div>

  <!-- Filters Panel -->
  <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
    <div class="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
      <!-- Day Presets -->
      <div class="flex flex-wrap items-center gap-1.5">
        <span class="text-xs font-semibold uppercase text-gray-400 mr-2 tracking-wider">Day:</span>
        <button *ngFor="let p of presets" (click)="selectPreset(p.id)"
          [class.bg-green-600]="selectedPreset === p.id"
          [class.text-white]="selectedPreset === p.id"
          [class.bg-gray-100]="selectedPreset !== p.id"
          [class.text-gray-700]="selectedPreset !== p.id"
          [class.hover:bg-gray-200]="selectedPreset !== p.id"
          class="px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5">
          <i class="fas {{ p.icon }} text-xs" [class.text-white]="selectedPreset === p.id" [class.text-gray-400]="selectedPreset !== p.id"></i>
          {{ p.label }}
        </button>
      </div>

      <!-- Custom Date & Zone Filter -->
      <div class="flex flex-wrap items-center gap-3">
        <div *ngIf="selectedPreset === 'custom'" class="flex items-center gap-1.5 bg-gray-50 border border-gray-300 rounded-lg px-2 py-1">
          <span class="text-xs text-gray-500">Date:</span>
          <input type="date" [(ngModel)]="customDate" (change)="onCustomDateChange()"
            class="bg-transparent text-xs text-gray-800 focus:outline-none" />
        </div>

        <div class="min-w-[180px]">
          <select [(ngModel)]="selectedZone" (change)="onZoneChange()"
            class="w-full text-xs bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-green-500 focus:outline-none">
            <option value="">All Geo Zones</option>
            <option *ngFor="let z of zones" [value]="z.id">{{ z.name }}</option>
          </select>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="flex flex-col items-center justify-center py-16 bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-3"></div>
    <p class="text-sm text-gray-500 font-medium">Crunching today's numbers...</p>
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage && !loading" class="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mb-6">
    <div class="flex items-center">
      <i class="fas fa-exclamation-circle text-red-500 mr-3"></i>
      <p class="text-sm text-red-700">{{ errorMessage }}</p>
    </div>
  </div>

  <ng-container *ngIf="data && !loading">
    <!-- Hero Row -->
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
      <div class="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 text-white shadow-sm flex flex-col justify-between relative overflow-hidden">
        <div class="absolute -right-2 -bottom-2 opacity-15 text-6xl text-white"><i class="fas fa-rupee-sign"></i></div>
        <span class="text-xs font-semibold text-amber-100 uppercase tracking-wider mb-2">Total Scrap Value</span>
        <div class="text-2xl font-bold">&#8377;{{ data.collectionInsights.totalScrapValue | number:'1.0-0' }}</div>
        <div class="text-xs text-amber-100 mt-1">{{ data.collectionInsights.totalCollections }} collections</div>
      </div>

      <div class="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-xl p-4 text-white shadow-sm flex flex-col justify-between relative overflow-hidden">
        <div class="absolute -right-2 -bottom-2 opacity-15 text-6xl text-white"><i class="fas fa-check-circle"></i></div>
        <span class="text-xs font-semibold text-emerald-100 uppercase tracking-wider mb-2">Fulfilled Today</span>
        <div class="text-2xl font-bold">{{ data.appointments.scheduledToday.fulfilled }} <span class="text-base font-semibold text-emerald-100">({{ pct(data.appointments.scheduledToday.fulfilled, data.appointments.scheduledToday.total) }})</span></div>
        <div class="text-xs text-emerald-100 mt-1">of {{ data.appointments.scheduledToday.total }} scheduled</div>
      </div>

      <div class="bg-gradient-to-br from-green-600 to-emerald-700 rounded-xl p-4 text-white shadow-sm flex flex-col justify-between relative overflow-hidden">
        <div class="absolute -right-2 -bottom-2 opacity-15 text-6xl text-white"><i class="fas fa-truck"></i></div>
        <span class="text-xs font-semibold text-green-100 uppercase tracking-wider mb-2">Agents On Duty</span>
        <div class="text-2xl font-bold">{{ data.agents.onDuty }} <span class="text-base font-semibold text-green-100">({{ pct(data.agents.onDuty, data.agents.totalActive) }})</span></div>
        <div class="text-xs text-green-100 mt-1">of {{ data.agents.totalActive }} active</div>
      </div>

      <div class="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl p-4 text-white shadow-sm flex flex-col justify-between relative overflow-hidden">
        <div class="absolute -right-2 -bottom-2 opacity-15 text-6xl text-white"><i class="fas fa-user-plus"></i></div>
        <span class="text-xs font-semibold text-indigo-100 uppercase tracking-wider mb-2">New Registrations</span>
        <div class="text-2xl font-bold">{{ data.newRegistrations }}</div>
        <div class="text-xs text-indigo-100 mt-1">all zones</div>
      </div>

      <div class="bg-gradient-to-br from-red-500 to-rose-700 rounded-xl p-4 text-white shadow-sm flex flex-col justify-between relative overflow-hidden">
        <div class="absolute -right-2 -bottom-2 opacity-15 text-6xl text-white"><i class="fas fa-map-marker-alt"></i></div>
        <span class="text-xs font-semibold text-red-100 uppercase tracking-wider mb-2">Unsupported Locations</span>
        <div class="text-2xl font-bold">{{ data.unsupportedLocations.total }}</div>
        <div class="text-xs text-red-100 mt-1">all zones</div>
      </div>
    </div>

    <!-- Appointments: Scheduled for Today / Booked Today / Agents, side by side -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
      <!-- Scheduled for Today -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div class="flex items-baseline justify-between mb-3">
          <div class="text-xs font-bold uppercase tracking-wider text-gray-500">Scheduled for today</div>
          <div class="text-xs text-gray-400">{{ data.appointments.scheduledToday.total }} on the books</div>
        </div>
        <div class="flex items-center gap-3">
          <div class="relative flex-shrink-0" style="width: 108px; height: 108px;">
            <canvas baseChart [data]="scheduledStatusChartData" [options]="doughnutOptions" type="doughnut"></canvas>
            <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span class="text-lg font-bold text-gray-900">{{ data.appointments.scheduledToday.total }}</span>
              <span class="text-[9px] text-gray-400 uppercase tracking-wide">Total</span>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-2 flex-1">
            <div class="rounded-lg border border-gray-200 border-l-4 border-l-green-500 px-2 py-1.5 flex items-center gap-1.5">
              <span class="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-check text-[10px]"></i></span>
              <div class="min-w-0">
                <span class="text-[11px] text-gray-500 block leading-tight">Fulfilled</span>
                <span class="text-sm font-bold text-green-700">{{ data.appointments.scheduledToday.fulfilled }}</span>
                <span class="text-[10px] text-gray-400">{{ pct(data.appointments.scheduledToday.fulfilled, data.appointments.scheduledToday.total) }}</span>
              </div>
            </div>
            <div class="rounded-lg border border-gray-200 border-l-4 border-l-amber-500 px-2 py-1.5 flex items-center gap-1.5">
              <span class="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-clock text-[10px]"></i></span>
              <div class="min-w-0">
                <span class="text-[11px] text-gray-500 block leading-tight">Pending</span>
                <span class="text-sm font-bold text-amber-700">{{ data.appointments.scheduledToday.pendingOrConfirmed }}</span>
                <span class="text-[10px] text-gray-400">{{ pct(data.appointments.scheduledToday.pendingOrConfirmed, data.appointments.scheduledToday.total) }}</span>
              </div>
            </div>
            <div class="rounded-lg border border-gray-200 border-l-4 border-l-red-500 px-2 py-1.5 flex items-center gap-1.5">
              <span class="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-ban text-[10px]"></i></span>
              <div class="min-w-0">
                <span class="text-[11px] text-gray-500 block leading-tight">Cancelled</span>
                <span class="text-sm font-bold text-red-700">{{ data.appointments.scheduledToday.cancelled }}</span>
                <span class="text-[10px] text-gray-400">{{ pct(data.appointments.scheduledToday.cancelled, data.appointments.scheduledToday.total) }}</span>
              </div>
            </div>
            <div class="rounded-lg border border-gray-200 border-l-4 border-l-red-700 px-2 py-1.5 flex items-center gap-1.5">
              <span class="w-6 h-6 rounded-full bg-red-100 text-red-800 flex items-center justify-center flex-shrink-0"><i class="fas fa-times-circle text-[10px]"></i></span>
              <div class="min-w-0">
                <span class="text-[11px] text-gray-500 block leading-tight">Rejected</span>
                <span class="text-sm font-bold text-red-800">{{ data.appointments.scheduledToday.rejected }}</span>
                <span class="text-[10px] text-gray-400">{{ pct(data.appointments.scheduledToday.rejected, data.appointments.scheduledToday.total) }}</span>
              </div>
            </div>
            <div class="rounded-lg border border-gray-200 border-l-4 border-l-gray-400 px-2 py-1.5 flex items-center gap-1.5 col-span-2">
              <span class="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-calendar-times text-[10px]"></i></span>
              <div class="min-w-0">
                <span class="text-[11px] text-gray-500 block leading-tight">Rescheduled away</span>
                <span class="text-sm font-bold text-gray-700">{{ data.appointments.scheduledToday.rescheduledAway }}</span>
                <span class="text-[10px] text-gray-400">{{ pct(data.appointments.scheduledToday.rescheduledAway, data.appointments.scheduledToday.total) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- New Appointments Booked Today -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div class="flex items-baseline justify-between mb-3">
          <div class="text-xs font-bold uppercase tracking-wider text-gray-500">Booked today</div>
          <div class="text-xs text-gray-400">{{ data.appointments.createdToday.total }} for any date</div>
        </div>
        <div class="flex items-center gap-3 mb-3">
          <div class="relative flex-shrink-0" style="width: 108px; height: 108px;">
            <canvas baseChart [data]="channelChartData" [options]="doughnutOptions" type="doughnut"></canvas>
            <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span class="text-lg font-bold text-gray-900">{{ data.appointments.createdToday.total }}</span>
              <span class="text-[9px] text-gray-400 uppercase tracking-wide">Total</span>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-2 flex-1">
            <div class="rounded-lg border border-gray-200 border-l-4 border-l-indigo-500 px-2 py-1 flex items-center gap-1.5">
              <span class="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-bullhorn text-[10px]"></i></span>
              <span class="text-[11px] text-gray-500 flex-1">Via ads</span>
              <span class="text-sm font-bold text-indigo-700">{{ data.appointments.createdToday.convertedViaAds }}</span>
              <span class="text-[10px] text-gray-400">{{ pct(data.appointments.createdToday.convertedViaAds, data.appointments.createdToday.total) }}</span>
            </div>
            <div class="rounded-lg border border-gray-200 border-l-4 border-l-green-500 px-2 py-1 flex items-center gap-1.5">
              <span class="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0"><i class="fab fa-whatsapp text-[10px]"></i></span>
              <span class="text-[11px] text-gray-500 flex-1">Via WhatsApp</span>
              <span class="text-sm font-bold text-green-700">{{ data.appointments.createdToday.convertedViaDirectWhatsApp }}</span>
              <span class="text-[10px] text-gray-400">{{ pct(data.appointments.createdToday.convertedViaDirectWhatsApp, data.appointments.createdToday.total) }}</span>
            </div>
            <div class="rounded-lg border border-gray-200 border-l-4 border-l-gray-400 px-2 py-1 flex items-center gap-1.5">
              <span class="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-leaf text-[10px]"></i></span>
              <span class="text-[11px] text-gray-500 flex-1">Organic</span>
              <span class="text-sm font-bold text-gray-700">{{ data.appointments.createdToday.withoutWhatsApp }}</span>
              <span class="text-[10px] text-gray-400">{{ pct(data.appointments.createdToday.withoutWhatsApp, data.appointments.createdToday.total) }}</span>
            </div>
          </div>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div class="rounded-lg border border-gray-200 border-l-4 border-l-teal-500 px-2 py-1.5 flex items-center gap-1.5">
            <span class="w-6 h-6 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-calendar-check text-[10px]"></i></span>
            <div class="min-w-0">
              <span class="text-[11px] text-gray-500 block leading-tight">Rescheduled in</span>
              <span class="text-sm font-bold text-teal-700">{{ data.appointments.createdToday.viaReschedule }}</span>
              <span class="text-[10px] text-gray-400">{{ pct(data.appointments.createdToday.viaReschedule, data.appointments.createdToday.total) }}</span>
            </div>
          </div>
          <div class="rounded-lg border border-gray-200 border-l-4 border-l-amber-500 px-2 py-1.5 flex items-center gap-1.5">
            <span class="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-hourglass-half text-[10px]"></i></span>
            <div class="min-w-0">
              <span class="text-[11px] text-gray-500 block leading-tight">Still pending</span>
              <span class="text-sm font-bold text-amber-700">{{ data.appointments.createdToday.pendingOrConfirmed }}</span>
              <span class="text-[10px] text-gray-400">{{ pct(data.appointments.createdToday.pendingOrConfirmed, data.appointments.createdToday.total) }}</span>
            </div>
          </div>
          <div class="rounded-lg border border-gray-200 border-l-4 border-l-red-500 px-2 py-1.5 flex items-center gap-1.5">
            <span class="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-ban text-[10px]"></i></span>
            <div class="min-w-0">
              <span class="text-[11px] text-gray-500 block leading-tight">Cancelled</span>
              <span class="text-sm font-bold text-red-700">{{ data.appointments.createdToday.cancelled }}</span>
              <span class="text-[10px] text-gray-400">{{ pct(data.appointments.createdToday.cancelled, data.appointments.createdToday.total) }}</span>
            </div>
          </div>
          <div class="rounded-lg border border-gray-200 border-l-4 border-l-red-700 px-2 py-1.5 flex items-center gap-1.5">
            <span class="w-6 h-6 rounded-full bg-red-100 text-red-800 flex items-center justify-center flex-shrink-0"><i class="fas fa-times-circle text-[10px]"></i></span>
            <div class="min-w-0">
              <span class="text-[11px] text-gray-500 block leading-tight">Rejected</span>
              <span class="text-sm font-bold text-red-800">{{ data.appointments.createdToday.rejected }}</span>
              <span class="text-[10px] text-gray-400">{{ pct(data.appointments.createdToday.rejected, data.appointments.createdToday.total) }}</span>
            </div>
          </div>
          <div class="rounded-lg border border-gray-200 border-l-4 border-l-gray-400 px-2 py-1.5 flex items-center gap-1.5">
            <span class="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-calendar-times text-[10px]"></i></span>
            <div class="min-w-0">
              <span class="text-[11px] text-gray-500 block leading-tight">Rescheduled out</span>
              <span class="text-sm font-bold text-gray-700">{{ data.appointments.createdToday.rescheduledSince }}</span>
              <span class="text-[10px] text-gray-400">{{ pct(data.appointments.createdToday.rescheduledSince, data.appointments.createdToday.total) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Agents -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div class="flex items-baseline justify-between mb-3">
          <div class="text-xs font-bold uppercase tracking-wider text-gray-500">Agents</div>
          <div class="text-xs text-gray-400">{{ data.agents.totalActive }} active</div>
        </div>
        <div class="flex items-center gap-3">
          <div class="relative flex-shrink-0" style="width: 108px; height: 108px;">
            <canvas baseChart [data]="agentDutyChartData" [options]="doughnutOptions" type="doughnut"></canvas>
            <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span class="text-lg font-bold text-gray-900">{{ data.agents.totalActive }}</span>
              <span class="text-[9px] text-gray-400 uppercase tracking-wide">Agents</span>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-2 flex-1">
            <div class="rounded-lg border border-gray-200 border-l-4 border-l-gray-400 px-2 py-1 flex items-center gap-1.5">
              <span class="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-users text-[10px]"></i></span>
              <span class="text-[11px] text-gray-500 flex-1">Total</span>
              <span class="text-sm font-bold text-gray-900">{{ data.agents.totalActive }}</span>
            </div>
            <div class="rounded-lg border border-gray-200 border-l-4 border-l-green-500 px-2 py-1 flex items-center gap-1.5">
              <span class="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-user-check text-[10px]"></i></span>
              <span class="text-[11px] text-gray-500 flex-1">On duty</span>
              <span class="text-sm font-bold text-green-700">{{ data.agents.onDuty }}</span>
              <span class="text-[10px] text-gray-400">{{ pct(data.agents.onDuty, data.agents.totalActive) }}</span>
            </div>
            <div class="rounded-lg border border-gray-200 border-l-4 border-l-amber-500 px-2 py-1 flex items-center gap-1.5">
              <span class="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-user-clock text-[10px]"></i></span>
              <span class="text-[11px] text-gray-500 flex-1">On leave</span>
              <span class="text-sm font-bold text-amber-700">{{ data.agents.onLeave }}</span>
              <span class="text-[10px] text-gray-400">{{ pct(data.agents.onLeave, data.agents.totalActive) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Collection Insights -->
    <div class="mb-6">
      <div class="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 px-1">Collection insights</div>
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div class="rounded-xl border border-gray-200 border-l-4 border-l-amber-500 px-3 py-2 flex items-center gap-2">
          <span class="w-7 h-7 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-recycle text-xs"></i></span>
          <div class="min-w-0">
            <span class="text-xs text-gray-500 block">Scrap value</span>
            <span class="text-base font-bold text-gray-900">&#8377;{{ data.collectionInsights.totalScrapValue | number:'1.0-0' }}</span>
            <span class="text-xs text-gray-400 ml-1">{{ pct(data.collectionInsights.totalScrapValue, data.collectionInsights.totalGrandTotal) }}</span>
          </div>
        </div>
        <div class="rounded-xl border border-gray-200 border-l-4 border-l-amber-500 px-3 py-2 flex items-center gap-2">
          <span class="w-7 h-7 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-boxes text-xs"></i></span>
          <div class="min-w-0">
            <span class="text-xs text-gray-500 block">Non-scrap value</span>
            <span class="text-base font-bold text-gray-900">&#8377;{{ data.collectionInsights.totalNonScrapValue | number:'1.0-0' }}</span>
            <span class="text-xs text-gray-400 ml-1">{{ pct(data.collectionInsights.totalNonScrapValue, data.collectionInsights.totalGrandTotal) }}</span>
          </div>
        </div>
        <div class="rounded-xl border border-gray-200 border-l-4 border-l-blue-500 px-3 py-2 flex items-center gap-2">
          <span class="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-percentage text-xs"></i></span>
          <div class="min-w-0">
            <span class="text-xs text-gray-500 block">Platform charges</span>
            <span class="text-base font-bold text-gray-900">&#8377;{{ data.collectionInsights.totalPlatformCharges | number:'1.0-0' }}</span>
            <span class="text-xs text-gray-400 ml-1">{{ pct(data.collectionInsights.totalPlatformCharges, data.collectionInsights.totalGrandTotal) }}</span>
          </div>
        </div>
        <div class="rounded-xl border border-gray-200 border-l-4 border-l-blue-500 px-3 py-2 flex items-center gap-2">
          <span class="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-truck text-xs"></i></span>
          <div class="min-w-0">
            <span class="text-xs text-gray-500 block">Collection charges</span>
            <span class="text-base font-bold text-gray-900">&#8377;{{ data.collectionInsights.totalCollectionCharges | number:'1.0-0' }}</span>
            <span class="text-xs text-gray-400 ml-1">{{ pct(data.collectionInsights.totalCollectionCharges, data.collectionInsights.totalGrandTotal) }}</span>
          </div>
        </div>
        <div class="rounded-xl border border-gray-200 border-l-4 border-l-teal-500 px-3 py-2 flex items-center gap-2">
          <span class="w-7 h-7 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-ticket-alt text-xs"></i></span>
          <div class="min-w-0">
            <span class="text-xs text-gray-500 block">Voucher commission</span>
            <span class="text-base font-bold text-gray-900">&#8377;{{ data.collectionInsights.totalVoucherCommission | number:'1.0-0' }}</span>
            <span class="text-xs text-gray-400 ml-1">{{ pct(data.collectionInsights.totalVoucherCommission, data.collectionInsights.totalGrandTotal) }}</span>
          </div>
        </div>
        <div class="rounded-xl border border-gray-200 border-l-4 border-l-teal-500 px-3 py-2 flex items-center gap-2">
          <span class="w-7 h-7 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0"><i class="fas fa-user-tag text-xs"></i></span>
          <div class="min-w-0">
            <span class="text-xs text-gray-500 block">Agent voucher commission</span>
            <span class="text-base font-bold text-gray-900">&#8377;{{ data.collectionInsights.totalAgentVoucherCommission | number:'1.0-0' }}</span>
            <span class="text-xs text-gray-400 ml-1">{{ pct(data.collectionInsights.totalAgentVoucherCommission, data.collectionInsights.totalGrandTotal) }}</span>
          </div>
        </div>
      </div>
    </div>
  </ng-container>
</div>
